import { createContext, useContext, type ReactNode } from "react";
import { useGlobalLoader } from "@/components/GlobalLoader";
import { useImpressaoConfig } from "../ImpressaoConfigContext";
import type { newHistoricoItem } from "@/types/new-historico-item";
import { alert } from "@/contexts/AlertModalContext";
import type { historicoItemResponse } from "@/types/historico-item-response-";
import impressao_socket_api from "@/services/impressao_socket_api";

export type HistoricoContextType = {
  criarHistorico: (
    historico: newHistoricoItem,
  ) => Promise<historicoItemResponse>;
  atualizarHistorico: (
    historico_updated: historicoItemResponse,
  ) => Promise<historicoItemResponse>;
};

export const HistoricoContext = createContext<HistoricoContextType | undefined>(
  undefined,
);

export function HistoricoProvider({ children }: { children: ReactNode }) {
  const { setIsLoading } = useGlobalLoader();
  const { impressaoConfig } = useImpressaoConfig();

  const atualizarHistorico = async (
    historico_updated: historicoItemResponse,
  ) => {
    try {
      setIsLoading(true);
      if (!impressaoConfig) {
        throw new Error("Configuração da conta não encontrada");
      }
      const response_historico =
        await impressao_socket_api.put<historicoItemResponse>(
          `/v1/historico/${historico_updated._id}`,
          historico_updated,
        );
      return response_historico.data;
    } catch (error) {
      alert.error("Erro ao atualizar histórico");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const criarHistorico = async (historico: newHistoricoItem) => {
    setIsLoading(true);
    try {
      if (!impressaoConfig) {
        throw new Error("Configuração da conta não encontrada");
      }
      const response_historico =
        await impressao_socket_api.post<historicoItemResponse>(
          `/v1/historico`,
          historico,
        );
      return response_historico.data;
    } catch (error) {
      alert.error("Erro ao criar histórico");
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <HistoricoContext.Provider
      value={{
        criarHistorico,
        atualizarHistorico,
      }}
    >
      {children}
    </HistoricoContext.Provider>
  );
}

export function useHistorico() {
  const context = useContext(HistoricoContext);
  if (!context) {
    throw new Error(
      "useHistorico deve ser usado dentro de um HistoricoProvider",
    );
  }
  return context;
}
